// ============================================================================
// Cotizaciones — the rules of the tracking screen, as pure functions.
//
//  • «Válida hasta»: the quote stands until `expiresAt`; when nobody set one it
//    is 30 days after it was sent. Past that date an open quote reads as
//    CANCELADA and its amount is CONGELADO.
//  • Temperatura: an open quote cools down over its validity — CALIENTE in the
//    first third, TIBIA in the middle, FRÍA in the last. The weighted value in
//    negotiation counts most of a hot quote and little of a cold one.
//  • Perdida: the quote was rejected, or its deal moved to a lost stage or to
//    «Follow Up Futuro» (parked for later).
//  • Ganada: accepted / approved, or its deal sits in a won stage.
// ============================================================================

import type { QuoteItem } from "./quoteMath";

export interface Quote {
  id: string;
  title?: string | null;
  amount?: number | string | null;
  currency?: string | null;
  status?: string | null;
  sentAt?: string | null;
  expiresAt?: string | null;
  createdAt?: unknown;
  company?: string | null;
  contact?: string | null;
  product?: string | null;
  dealStageLabel?: string | null;
  pipelineLabel?: string | null;
  source?: string | null;
  documentUrl?: string | null;
  /** Negocio del que salió la cotización. */
  dealId?: string | null;
  /** Un adjunto importado de HubSpot usado como documento de la cotización. */
  attachmentId?: string | null;
  attachmentName?: string | null;
  document?: { name?: string; contentType?: string; size?: number; uploadedAt?: string; generated?: boolean } | null;
  notes?: string | null;
  // --- Cotizaciones con renglones (#1545) ---
  items?: QuoteItem[] | null;
  subtotal?: number | null;
  discountTotal?: number | null;
  taxTotal?: number | null;
  total?: number | null;
  globalDiscountPct?: number | null;
  hasUnpriced?: boolean | null;
  /** COT-AAAAMMDD-XXXX. */
  folio?: string | null;
  storeSlug?: string | null;
  requestId?: string | null;
  /** Cuándo la pidió el cliente en la tienda. */
  requestedAt?: string | null;
  leadId?: string | null;
  contactId?: string | null;
  companyId?: string | null;
  terms?: string | null;
  customer?: { name?: string | null; company?: string | null; email?: string | null; phone?: string | null; fleetSize?: string | null } | null;
  statusHistory?: { from?: string | null; to: string; at: string; by?: string | null; via?: string | null; reason?: string | null }[] | null;
  acceptedAt?: string | null;
  acceptedVia?: string | null;
  rejectedAt?: string | null;
  rejectReason?: string | null;
  sentTo?: string | null;
  /** Versión de lo que acepta el cliente; sube al editar renglones, vigencia o condiciones. */
  revision?: number | null;
  /** Lo calcula el API al leer una: «vencida» si pasó su vigencia. */
  effectiveStatus?: string | null;
  publicUrl?: string | null;
  deal?: { id: string; title?: string | null; stage?: string | null; amount?: number | null } | null;
}

/** ¿Es una cotización armada con renglones (o pedida en la tienda)? Se abre en el armador. */
export const isItemized = (q: Quote): boolean => Array.isArray(q.items) || q.source === "store" || q.source === "manual";

/** Espejo de un negocio de HubSpot: no se edita en el armador. */
export const isMirrorQuote = (q: Quote): boolean => String(q.id || "").startsWith("hsdeal_");

export type QuoteBucket = "caliente" | "tibia" | "fria" | "congelada" | "perdida" | "ganada";

export const QUOTE_VALIDITY_DAYS = 30;
const DAY = 24 * 60 * 60 * 1000;

/** How much of a quote's amount counts as "in negotiation", by bucket. */
export const BUCKET_WEIGHT: Record<QuoteBucket, number> = {
  caliente: 0.75,
  tibia: 0.5,
  fria: 0.25,
  congelada: 0,
  perdida: 0,
  ganada: 1,
};

export const BUCKET_META: Record<QuoteBucket, { label: string; icon: string; tone: string; dot: string }> = {
  caliente: { label: "Caliente", icon: "ri-fire-line", tone: "bg-red-500/15 text-red-500", dot: "bg-red-500" },
  tibia: { label: "Tibia", icon: "ri-temp-hot-line", tone: "bg-amber-500/15 text-amber-600", dot: "bg-amber-500" },
  fria: { label: "Fría", icon: "ri-temp-cold-line", tone: "bg-sky-500/15 text-sky-600", dot: "bg-sky-500" },
  congelada: { label: "Congelada", icon: "ri-snowy-line", tone: "bg-slate-400/20 text-slate-500", dot: "bg-slate-400" },
  perdida: { label: "Perdida", icon: "ri-close-circle-line", tone: "bg-gray-500/15 text-gray-500", dot: "bg-gray-500" },
  ganada: { label: "Ganada", icon: "ri-trophy-line", tone: "bg-emerald-500/15 text-emerald-600", dot: "bg-emerald-500" },
};

export const amountOf = (q: Quote): number => {
  const n = Number(q.amount);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const ms = (value?: string | null): number | null => {
  const t = value ? Date.parse(value) : NaN;
  return Number.isFinite(t) ? t : null;
};

/** When the quote went out (falls back to nothing: a quote with no date has no temperature clock). */
export const sentMs = (q: Quote): number | null => ms(q.sentAt);

/** «Válida hasta»: the date set on the quote, else 30 days after it was sent. */
export function validUntilMs(q: Quote): number | null {
  const own = ms(q.expiresAt);
  if (own !== null) return own;
  const sent = sentMs(q);
  return sent === null ? null : sent + QUOTE_VALIDITY_DAYS * DAY;
}

const WON_STATUS = new Set(["aceptada", "aprobada"]);
const LOST_STATUS = new Set(["rechazada", "cancelada", "vencida"]);
const LOST_STAGE = /perdid|\blost\b|descartad|follow|seguimiento/i;
const WON_STAGE = /ganad|\bwon\b/i;

/** Past its «Válida hasta» without having been won or lost. */
export function isExpired(q: Quote, now: number): boolean {
  const until = validUntilMs(q);
  return until !== null && until < now;
}

/**
 * Where the quote's amount is counted. Won and lost are decided first (they are
 * final); then an expired quote is frozen; otherwise it is hot, warm or cold by
 * how far into its validity it is.
 */
export function bucketOf(q: Quote, now: number): QuoteBucket {
  const status = String(q.status ?? "").toLowerCase();
  const stage = q.dealStageLabel ?? "";
  // The deal decides first: one parked in «Follow Up Futuro» or lost is lost even
  // if its quote still says accepted (a portal can have that stage flagged as won).
  if (LOST_STAGE.test(stage) || status === "rechazada") return "perdida";
  if (WON_STATUS.has(status) || WON_STAGE.test(stage)) return "ganada";
  if (status === "cancelada" || status === "vencida" || isExpired(q, now)) return "congelada";

  const sent = sentMs(q);
  const until = validUntilMs(q);
  if (sent === null || until === null || until <= sent) return "caliente";
  const elapsed = (now - sent) / (until - sent);
  if (elapsed < 1 / 3) return "caliente";
  if (elapsed < 2 / 3) return "tibia";
  return "fria";
}

/**
 * The status to show. A quote still marked as sent (or draft) whose validity
 * passed reads as «cancelada»: nobody has to remember to close it.
 */
export function effectiveStatus(q: Quote, now: number): string {
  const status = String(q.status ?? "").toLowerCase() || "borrador";
  if (WON_STATUS.has(status) || LOST_STATUS.has(status)) return status;
  return isExpired(q, now) ? "cancelada" : status;
}

/** Whole days left until «Válida hasta» (negative once it passed); null with no date. */
export function daysLeft(q: Quote, now: number): number | null {
  const until = validUntilMs(q);
  return until === null ? null : Math.ceil((until - now) / DAY);
}

// --- period -----------------------------------------------------------------

export type PeriodPreset = "this_month" | "last_month" | "last_3_months" | "this_year" | "all" | "custom";

export interface Period {
  /** Inclusive start, exclusive end, in ms; null = open on that side. */
  from: number | null;
  to: number | null;
}

export const PERIOD_LABEL: Record<PeriodPreset, string> = {
  this_month: "Este mes",
  last_month: "Mes pasado",
  last_3_months: "Últimos 3 meses",
  this_year: "Este año",
  all: "Todo",
  custom: "Personalizado",
};

/** The range a preset means, in the viewer's local time. `custom` uses the two ISO dates (inclusive). */
export function periodOf(preset: PeriodPreset, now: number, customFrom?: string, customTo?: string): Period {
  const d = new Date(now);
  const start = (y: number, m: number) => new Date(y, m, 1).getTime();
  switch (preset) {
    case "this_month":
      return { from: start(d.getFullYear(), d.getMonth()), to: start(d.getFullYear(), d.getMonth() + 1) };
    case "last_month":
      return { from: start(d.getFullYear(), d.getMonth() - 1), to: start(d.getFullYear(), d.getMonth()) };
    case "last_3_months":
      return { from: start(d.getFullYear(), d.getMonth() - 2), to: start(d.getFullYear(), d.getMonth() + 1) };
    case "this_year":
      return { from: start(d.getFullYear(), 0), to: start(d.getFullYear() + 1, 0) };
    case "custom": {
      const from = customFrom ? new Date(`${customFrom}T00:00:00`).getTime() : NaN;
      const to = customTo ? new Date(`${customTo}T00:00:00`).getTime() : NaN;
      return { from: Number.isFinite(from) ? from : null, to: Number.isFinite(to) ? to + DAY : null };
    }
    default:
      return { from: null, to: null };
  }
}

/**
 * Months the period spans, for «cotizaciones por mes». An open-ended period
 * («Todo») is measured from the first to the last quote sent.
 */
export function monthsIn(period: Period, quotes: Quote[]): number {
  let from = period.from;
  let to = period.to;
  if (from === null || to === null) {
    const dates = quotes.map(sentMs).filter((t): t is number => t !== null);
    if (!dates.length) return 1;
    from = from ?? Math.min(...dates);
    to = to ?? Math.max(...dates) + 1;
  }
  const a = new Date(from);
  const b = new Date(to - 1);
  return Math.max(1, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1);
}

// --- filters ----------------------------------------------------------------

export type ExpiryFilter = "all" | "valid" | "soon" | "expired";

export const EXPIRY_LABEL: Record<ExpiryFilter, string> = {
  all: "Todas",
  valid: "Vigentes",
  soon: "Vencen en 7 días",
  expired: "Vencidas",
};

export interface QuoteFilters {
  period: Period;
  /** Deal stage label; "" = every stage. */
  stage: string;
  expiry: ExpiryFilter;
  query: string;
}

export function filterQuotes(quotes: Quote[], f: QuoteFilters, now: number): Quote[] {
  const q = f.query.trim().toLowerCase();
  return quotes.filter((quote) => {
    const sent = sentMs(quote);
    if (f.period.from !== null && (sent === null || sent < f.period.from)) return false;
    if (f.period.to !== null && (sent === null || sent >= f.period.to)) return false;
    if (f.stage && (quote.dealStageLabel ?? "") !== f.stage) return false;
    if (f.expiry !== "all") {
      const left = daysLeft(quote, now);
      if (left === null) return false;
      if (f.expiry === "expired" && left >= 0) return false;
      if (f.expiry === "valid" && left < 0) return false;
      if (f.expiry === "soon" && (left < 0 || left > 7)) return false;
    }
    if (q && ![quote.title, quote.company, quote.contact].some((v) => (v ?? "").toLowerCase().includes(q))) return false;
    return true;
  });
}

/** Deal stages present on the quotes, for the stage filter. */
export const stagesOf = (quotes: Quote[]): string[] =>
  [...new Set(quotes.map((q) => (q.dealStageLabel ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));

// --- KPIs -------------------------------------------------------------------

export interface QuoteKpis {
  count: number;
  /** Quotes sent per month over the period. */
  perMonth: number;
  months: number;
  /** Mean amount of the quotes that HAVE an amount; a quote at 0 is "not priced yet", not a free one. */
  averageValue: number;
  priced: number;
  /** Sum of the amounts still in play: hot + warm + cold. */
  inNegotiation: number;
  /** The same, weighted by temperature. */
  weighted: number;
  buckets: Record<QuoteBucket, { count: number; total: number }>;
}

export function quoteKpis(quotes: Quote[], period: Period, now: number): QuoteKpis {
  const buckets = Object.fromEntries(
    (Object.keys(BUCKET_WEIGHT) as QuoteBucket[]).map((b) => [b, { count: 0, total: 0 }]),
  ) as QuoteKpis["buckets"];
  let sum = 0;
  let priced = 0;
  let weighted = 0;
  for (const quote of quotes) {
    const bucket = bucketOf(quote, now);
    const amount = amountOf(quote);
    buckets[bucket].count += 1;
    buckets[bucket].total += amount;
    if (amount > 0) {
      sum += amount;
      priced += 1;
    }
    if (bucket === "caliente" || bucket === "tibia" || bucket === "fria") weighted += amount * BUCKET_WEIGHT[bucket];
  }
  const months = monthsIn(period, quotes);
  return {
    count: quotes.length,
    perMonth: quotes.length / months,
    months,
    averageValue: priced ? sum / priced : 0,
    priced,
    inNegotiation: buckets.caliente.total + buckets.tibia.total + buckets.fria.total,
    weighted,
    buckets,
  };
}
